export const SSO_PROVIDER =
  process.env.NODE_ENV === 'development' ? 'console_dev' : 'console';

export const LOGIN_PATH = '/login';
export const REGISTER_PATH = '/register';

/** 无需登录即可访问的认证相关路径 */
export const PUBLIC_AUTH_PATHS = new Set([LOGIN_PATH, REGISTER_PATH]);

export function isPublicAuthPath(pathname: string): boolean {
  return PUBLIC_AUTH_PATHS.has(pathname);
}

/** 不应作为登录回跳目标的路径（如 404） */
export const LOGIN_REDIRECT_BLOCKLIST = new Set(['/404']);

export function getRedirectPathname(path: string): string {
  return path.trim().split('?')[0] || '/';
}

export function isAllowedLoginRedirectPath(path: string): boolean {
  const trimmed = path.trim();
  if (!trimmed.startsWith('/')) {
    return false;
  }
  const pathname = getRedirectPathname(trimmed);
  if (isPublicAuthPath(pathname)) {
    return false;
  }
  if (LOGIN_REDIRECT_BLOCKLIST.has(pathname)) {
    return false;
  }
  return true;
}

/** 合法则返回原路径，否则 undefined（不带 redirect） */
export function resolveLoginRedirectTarget(
  path?: string | null,
): string | undefined {
  const trimmed = path?.trim();
  if (!trimmed || !isAllowedLoginRedirectPath(trimmed)) {
    return undefined;
  }
  return trimmed;
}

/** SSO login-sso 回跳路径：优先读登录页 redirect 参数；404 等无效页不传 */
export function getSsoRedirectUri(): string | undefined {
  const redirect = new URLSearchParams(window.location.search)
    .get('redirect')
    ?.trim();
  return resolveLoginRedirectTarget(redirect);
}

/** 当前页面路径（pathname + search）；无效页或已在登录页时不带 redirect */
export function getCurrentPagePath(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const { pathname, search } = window.location;
  if (pathname === LOGIN_PATH) {
    return getSsoRedirectUri();
  }
  return resolveLoginRedirectTarget(`${pathname}${search}`);
}

/** 构建登录页 URL，无合法回跳目标时不带 redirect 参数 */
export function buildLoginUrl(redirectPath?: string): string {
  const target =
    redirectPath !== undefined
      ? resolveLoginRedirectTarget(redirectPath)
      : getCurrentPagePath();
  if (!target) {
    return LOGIN_PATH;
  }
  return `${LOGIN_PATH}?redirect=${encodeURIComponent(target)}`;
}

/** 清理 token 并跳转登录（保留当前页为 redirect，供 SSO redirectUri 使用） */
export function redirectToLoginPage(): void {
  clearAuthTokens();
  if (
    typeof window === 'undefined' ||
    isPublicAuthPath(window.location.pathname)
  ) {
    return;
  }
  window.location.href = buildLoginUrl();
}

export const getUrlTokenParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  let accessToken = urlParams.get('accessToken');
  let refreshToken = urlParams.get('refreshToken');

  const hash = window.location.hash.slice(1);
  const [path, hashQuery = ''] = hash.split('?');
  const hashParams = new URLSearchParams(hashQuery);

  if (!accessToken) {
    accessToken = hashParams.get('accessToken');
  }
  if (!refreshToken) {
    refreshToken = hashParams.get('refreshToken');
  }

  return {
    accessToken,
    refreshToken,
    path,
  };
};

export const persistTokensFromUrl = () => {
  const { accessToken, refreshToken } = getUrlTokenParams();
  if (!accessToken) {
    return false;
  }

  localStorage.setItem('accessToken', accessToken);
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  }

  const cleanUrl = `${window.location.origin}${window.location.pathname}`;
  window.history.replaceState({}, document.title, cleanUrl);
  return true;
};

export const persistAuthTokens = (tokens: {
  accessToken: string;
  refreshToken?: string;
}) => {
  localStorage.setItem('accessToken', tokens.accessToken);
  if (tokens.refreshToken) {
    localStorage.setItem('refreshToken', tokens.refreshToken);
  }
};

export const clearAuthTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};
